<template>
  <div class="form container">
    <p>{{ $t(`message.form.requiredFields`) }}</p>
    <div class="mb-3">
      <HoneypotField
        :ref="this.honeypotRef"
        :fieldValue="honeypotValue"
        @update:fieldValue="setHoneypotValue"
      ></HoneypotField>
      <label for="firstnameInput" class="form-label"
        >{{ $t(`message.firstname`) }}<span class="red">*</span></label
      >
      <input
        class="form-control input"
        id="firstnameInput"
        v-model="form.firstname"
        :class="{
          invalid: v$.form.firstname.$dirty ? v$.form.firstname.$error : null,
        }"
        @keyup.enter="next('lastnameInput')"
      />
      <div
        class="invalid-input-text"
        v-if="v$.form.firstname.$dirty ? v$.form.firstname.$error : false"
      >
        {{
          v$.form.firstname.required.$invalid
            ? $t(`message.form.requiredFirstname`)
            : ""
        }}
        {{
          v$.form.firstname.maxLength.$invalid
            ? $t(`message.form.tooLong`, { value: "80" })
            : ""
        }}
      </div>
    </div>

    <div class="mb-3">
      <label for="lastnameInput" class="form-label">{{
        $t(`message.lastname`)
      }}</label
      ><span class="red">*</span>
      <input
        class="form-control input"
        v-model="form.lastname"
        id="lastnameInput"
        ref="lastnameInput"
        :class="{
          invalid: v$.form.lastname.$dirty ? v$.form.lastname.$error : null,
        }"
        @keyup.enter="next('emailInput')"
      />
      <div
        class="invalid-input-text"
        v-if="v$.form.lastname.$dirty ? v$.form.lastname.$error : false"
      >
        {{ $t(`message.form.requiredLastname`) }}
      </div>
    </div>
    <div class="mb-3">
      <label for="emailInput" class="form-label">{{
        $t(`message.email`)
      }}</label
      ><span class="red">*</span>
      <input
        class="form-control input"
        id="emailInput"
        ref="emailInput"
        type="email"
        v-model="form.email"
        :class="{
          invalid: v$.form.email.$dirty ? v$.form.email.$error : null,
        }"
        @keyup.enter="
          next(originForm !== 'student' ? 'organizationInput' : 'messageInput')
        "
      />
      <div
        class="invalid-input-text"
        v-if="v$.form.email.$dirty ? v$.form.email.$error : false"
      >
        {{
          v$.form.email.required.$invalid
            ? $t(`message.form.requiredEmail`)
            : ""
        }}
        {{ v$.form.email.email.$invalid ? $t(`message.form.validEmail`) : "" }}
      </div>
    </div>
    <div class="mb-3" v-if="originForm !== 'student'">
      <label for="organizationInput" class="form-label">{{
        $t(`message.organization`)
      }}</label>
      <input
        class="form-control input"
        id="organizationInput"
        ref="organizationInput"
        v-model="form.organization"
        @keyup.enter="next('cityInput')"
      />
    </div>
    <div class="mb-3" v-if="originForm !== 'student'">
      <label for="cityInput" class="form-label"
        >{{ $t(`message.city`)
        }}<span v-if="originForm === 'teacher'" class="red">*</span></label
      >

      <input
        class="form-control input"
        id="cityInput"
        ref="cityInput"
        v-model="form.city"
        @keyup.enter="next('messageInput')"
      />
      <div
        class="invalid-input-text"
        v-if="
          v$.form.city.$dirty && originForm === 'teacher'
            ? v$.form.city.$error
            : false
        "
      >
        {{ $t(`message.form.requiredCity`) }}
      </div>
    </div>

    <div class="mb-3">
      <label for="messageInput" class="form-label">{{
        $t(`message.message`)
      }}</label
      ><span class="red">*</span>

      <textarea
        class="form-control input"
        v-model="form.message"
        ref="messageInput"
        rows="5"
        :class="{
          invalid: v$.form.message.$dirty ? v$.form.message.$error : null,
        }"
      />
      <div
        class="invalid-input-text"
        v-if="v$.form.message.$dirty ? v$.form.message.$error : false"
      >
        {{ $t(`message.form.requiredMessage`) }}
      </div>
      <div class="privacy-policy">
        {{ $t(`message.dataContactMessage`) }}
        <a v-bind:href="$t(`home.PrivacyPolicy.ressourceUrl`)" target="_blank">
          {{ $t(`message.privacyPolicyMessage`) }}
        </a>
      </div>
    </div>

    <div class="row">
      <div class="col text-center">
        <button type="submit" class="btn btn-pink" @click="submitForm">
          {{ $t(`message.send`) }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import { v4 as uuidv4 } from "uuid";
import HoneypotField from "../HoneypotField.vue";

import {
  required,
  requiredIf,
  helpers,
  email,
  maxLength,
} from "@vuelidate/validators";

export default {
  name: "ContactForm",
  props: {
    originForm: String,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  components: {
    HoneypotField,
  },
  data() {
    return {
      form: {
        firstname: "",
        lastname: "",
        email: "",
        message: "",
        organization: "",
        city: "",
      },
      honeypotValue: "",
    };
  },
  validations() {
    const { withAsync } = helpers;
    return {
      form: {
        firstname: {
          required,
          maxLength: maxLength(80),
        },
        lastname: {
          required,
          maxLength: maxLength(80),
        },
        email: {
          required,
          email,
        },
        city: {
          required: withAsync(requiredIf(() => this.originForm === "teacher")),
        },
        message: {
          required,
        },
      },
    };
  },
  methods: {
    getHoneypotRef() {
      this.honeypotRef = uuidv4();
    },
    setHoneypotValue(value) {
      this.honeypotValue = value;
    },
    next(nextValue) {
      switch (nextValue) {
        case "lastnameInput":
          this.$refs["lastnameInput"].focus();
          break;
        case "emailInput":
          this.$refs["emailInput"].focus();
          break;
        case "messageInput":
          this.$refs["messageInput"].focus();
          break;
        case "organizationInput":
          this.$refs["organizationInput"].focus();
          break;
        case "cityInput":
          this.$refs["cityInput"].focus();
          break;
        default:
          break;
      }
    },
    resetForm() {
      this.form = {
        firstname: "",
        lastname: "",
        email: "",
        message: "",
        organization: "",
        city: "",
      };
      this.v$.$reset();
    },
    submitForm() {
      // Captcha Honeypot validation
      var isValidHoneypot = this.$refs[this.honeypotRef].validate();
      this.v$.$touch();
      if (!this.v$.$invalid) {
        if (isValidHoneypot) {
          let formData = {
            firstname: this.form.firstname,
            lastname: this.form.lastname,
            email_from: this.form.email,
            message: this.form.message,
            origin_form: this.originForm,
            lang: this.$i18n.locale,
          };
          if (this.originForm !== "student") {
            this.form.city ? (formData.city = this.form.city) : null;
            this.form.organization
              ? (formData.organization = this.form.organization)
              : null;
          }

          this.$store
            .dispatch("contactForm/sendMail", formData)
            .then(() => {
              this.$store.dispatch("notifications/addSuccess", {
                message: this.$t(`message.messageSent`),
              });
              this.resetForm();
            })
            .catch((error) => {
              if (error.response) {
                if (error.response.status === 429) {
                  this.$store.dispatch("notifications/addError", {
                    message: this.$t(`message.toManyRequests`),
                  });
                } else if (error.response.status === 422) {
                  this.$store.dispatch("notifications/addError", {
                    message: this.$t(`message.form.invalidForm`),
                  });
                } else {
                  this.$store.dispatch("notifications/addError", {
                    message: this.$t(`message.unknown`),
                  });
                }
              }
            });
        } else {
          this.$store.dispatch("notifications/addError", {
            message: this.$t(`message.form.invalidForm`),
          });
        }
      }
    },
  },
  created() {
    this.getHoneypotRef();
  },
};
</script>

<style scoped lang="scss">
.form {
  .btn-pink {
    padding-left: auto;
    padding-right: auto;
    background-color: $secondaryColor;
    color: $whiteColor;
    width: 60%;
  }
  .red {
    color: red;
    font-weight: bold;
    font-size: 1.2rem;
  }
  .invalid-input-text {
    font-size: 0.8em;
    color: red;
    width: 200px;
    margin-top: 4px;
  }
}

.privacy-policy {
  display: flex;
  flex-direction: column;
  margin-top: 1rem;
  text-align: center;
  font-size: 0.8rem;
  a {
    color: inherit;
  }
}
</style>
